import { BenchPanel } from '../components/BenchPanel'
import { BookValueChart } from '../components/BookValueChart'
import { CapRejectionsFeed } from '../components/CapRejectionsFeed'
import { CleanInceptionBanner } from '../components/CleanInceptionBanner'
import { DormancyPanel } from '../components/DormancyPanel'
import { DossierABCard } from '../components/DossierABCard'
import { HoldingsPanel } from '../components/HoldingsPanel'
import { KillEntriesCard } from '../components/KillEntriesCard'
import { MirrorFidelityPanel } from '../components/MirrorFidelityPanel'
import { OverlayCard } from '../components/OverlayCard'
import { ResearchCadenceStrip } from '../components/ResearchCadenceStrip'
import { RiskBackstopCard } from '../components/RiskBackstopCard'
import { ScorecardTable } from '../components/ScorecardTable'
import { SectorPanel } from '../components/SectorPanel'
import { SubscriberFeedCard } from '../components/SubscriberFeedCard'
import { TaxSummaryCard } from '../components/TaxSummaryCard'
import { ValvePanel } from '../components/ValvePanel'
import { ROLE_LABEL } from '../components/desk/bookModuleHooks'
import type { Book } from '../types'
import '../components/Dashboard.css'
import './BookPage.css'

/**
 * Phone body of the per-book detail page (the pre-desk page body, unchanged):
 * a header + common panels (scorecard, overlay, tax) for every role, plus a
 * role-specific section — research picking analytics, the validation canary,
 * or live containment.
 */
export function BookLegacyBody({ book }: { book: Book }) {
  const status = book.bookStatus
  const isDormant = status === null
  const isHalted = status?.halted === true

  let statusText: string
  let statusCls: string
  if (isDormant) {
    statusText = 'dormant · not in engine'
    statusCls = 'muted'
  } else if (isHalted) {
    statusText = status.halt_reason ? `halted: ${status.halt_reason}` : 'halted'
    statusCls = 'warn'
  } else {
    statusText = 'running'
    statusCls = 'gain'
  }

  return (
    <main className="dashboard-content book-page">
      <section className="dash-zone">
        <div className="book-page-header card">
          <div className="book-page-header-top">
            <span className={`book-card-role-badge book-card-role-badge--${book.role}`}>
              {ROLE_LABEL[book.role]}
            </span>
            <span className={`book-card-status book-card-status--${statusCls}`}>
              <span className="book-card-status-dot" aria-hidden="true" />
              {statusText}
            </span>
          </div>
          <h1 className="h1">{book.label ?? `Account ${book.id}`}</h1>
          <div className="book-page-meta">
            <span className="mono">{book.account_id}</span>
            <span>·</span>
            <span>{book.broker_mode}</span>
            <span>·</span>
            <span>Overlay v<span className="num">{status?.overlay_version ?? '—'}</span></span>
          </div>
        </div>
      </section>

      <section className="dash-zone">
        <h2 className="dash-zone-title">Book value</h2>
        <div className="dash-zone-grid">
          <BookValueChart accountId={book.id} />
        </div>
      </section>

      <section className="dash-zone">
        <h2 className="dash-zone-title">Risk backstop</h2>
        <div className="dash-zone-grid">
          <RiskBackstopCard accountId={book.id} />
        </div>
      </section>

      <section className="dash-zone">
        <h2 className="dash-zone-title">Scorecard &amp; config</h2>
        <div className="dash-zone-grid">
          <HoldingsPanel accountId={book.id} />
        </div>
        <div className="dash-zone-grid">
          <ScorecardTable accountId={book.id} mirrorsSourceId={book.pick_source_account_id} />
        </div>
        <div className="dash-zone-grid dash-zone-grid--2">
          <OverlayCard accountId={book.id} />
          <TaxSummaryCard accountId={book.id} mirrorsSourceId={book.pick_source_account_id} />
        </div>
      </section>

      {(book.role === 'research' || book.role === 'validation') && (
        <section className="dash-zone">
          <h2 className="dash-zone-title">Sector</h2>
          <div className="dash-zone-grid">
            <SectorPanel accountId={book.id} />
          </div>
        </section>
      )}

      {book.role === 'research' && (
        <section className="dash-zone">
          <h2 className="dash-zone-title">Picking analytics</h2>
          <div className="dash-zone-grid dash-zone-grid--2">
            <ResearchCadenceStrip accountId={book.id} />
            <BenchPanel accountId={book.id} />
          </div>
          <div className="dash-zone-grid">
            <DossierABCard accountId={book.id} />
            <ValvePanel accountId={book.id} />
          </div>
        </section>
      )}

      {book.role === 'validation' && (
        <section className="dash-zone">
          <h2 className="dash-zone-title">Validation · platform canary</h2>
          <div className="dash-zone-grid">
            <CleanInceptionBanner book={book} />
          </div>
          <div className="dash-zone-grid dash-zone-grid--2">
            <MirrorFidelityPanel book={book} />
            <SubscriberFeedCard book={book} />
          </div>
        </section>
      )}

      {book.role === 'live' && (
        <section className="dash-zone">
          <h2 className="dash-zone-title">Live · containment</h2>
          <div className="dash-zone-grid">
            <DormancyPanel book={book} />
          </div>
          <div className="dash-zone-grid dash-zone-grid--2">
            <KillEntriesCard book={book} />
            <CapRejectionsFeed book={book} />
          </div>
        </section>
      )}
    </main>
  )
}
